import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Brain, Sparkles, Loader2, Lock, Download, FileText, FileCode, AlertCircle, Copy, Save, Coins, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Math as MathFormula } from "@/components/Math";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSupercalcCredits } from "@/hooks/useSupercalcCredits";
// Helper function to clean any remaining markdown from response
const cleanResponseText = (text: string): string => {
  let cleaned = text;
  
  // Remove markdown headers but preserve as styled sections
  cleaned = cleaned.replace(/^#{1,6}\s+(.+)$/gm, '\n$1\n');
  
  // Remove bold/italic markdown
  cleaned = cleaned.replace(/\*\*\*(.+?)\*\*\*/g, '$1');
  cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '$1');
  cleaned = cleaned.replace(/__(.+?)__/g, '$1');
  
  // Remove horizontal rules
  cleaned = cleaned.replace(/^[-*_]{3,}$/gm, '');
  
  // Clean bullet points
  cleaned = cleaned.replace(/^[\*\-]\s+/gm, '• ');
  
  // Clean up excessive newlines
  cleaned = cleaned.replace(/\n{4,}/g, '\n\n\n');
  
  return cleaned;
};

// Helper function to format text parts with styling (headings, bold sections)
const formatTextContent = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const lines = text.split('\n');
  
  lines.forEach((line, lineIndex) => {
    const trimmedLine = line.trim();
    
    // Check for section headings (capitalized text ending with colon)
    const isHeading = trimmedLine && 
      trimmedLine.endsWith(':') && 
      trimmedLine.length < 80 && 
      /^[A-Z]/.test(trimmedLine) &&
      !trimmedLine.includes('$');
    
    if (isHeading) {
      nodes.push(
        <div key={`heading-${lineIndex}`} className="font-bold text-lg text-foreground mt-6 mb-2 border-b border-border/30 pb-1">
          {trimmedLine}
        </div>
      );
    } else if (trimmedLine.startsWith('•') || /^\d+\.\s/.test(trimmedLine)) {
      // Bullet points or numbered lists
      nodes.push(
        <div key={`list-${lineIndex}`} className="ml-4 my-1 text-foreground/90">
          {line}
        </div>
      );
    } else if (trimmedLine === '') {
      // Blank line - add spacing
      nodes.push(<div key={`space-${lineIndex}`} className="h-3" />);
    } else {
      // Regular text
      nodes.push(
        <span key={`text-${lineIndex}`} className="text-foreground/90">
          {line}{lineIndex < lines.length - 1 ? '\n' : ''}
        </span>
      );
    }
  });
  
  return nodes;
};

// Helper function to parse text with LaTeX notation
const parseLatex = (text: string) => {
  // First clean any markdown artifacts
  let cleanedText = cleanResponseText(text);
  
  // Fix display math that has unclosed or broken delimiters
  // Handle $$ on separate lines with content between
  cleanedText = cleanedText.replace(/\$\$\s*\n\s*/g, '$$');
  cleanedText = cleanedText.replace(/\s*\n\s*\$\$/g, '$$');
  
  // Fix display math followed immediately by text (missing closing $$)
  // Look for $$ content that doesn't have a closing $$ before next $$
  cleanedText = cleanedText.replace(/\$\$([^$]+?)\$(?!\$)/g, '$$$1$');
  
  // Handle cases where content has $ at end instead of $$
  cleanedText = cleanedText.replace(/\$\$([^$]*?[^$])\$/g, (match, content) => {
    if (content.includes('$$')) return match;
    return `$$${content}$$`;
  });
  
  const parts: Array<{ type: 'text' | 'inline' | 'display'; content: string }> = [];
  
  // First, extract all display math blocks ($$...$$)
  const displayRegex = /\$\$([\s\S]*?)\$\$/g;
  
  let lastIndex = 0;
  let match;
  
  while ((match = displayRegex.exec(cleanedText)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      const textBefore = cleanedText.slice(lastIndex, match.index);
      parseInlineMath(textBefore, parts);
    }
    
    // Add display math
    const mathContent = match[1].trim();
    if (mathContent) {
      parts.push({ type: 'display', content: mathContent });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last display math
  if (lastIndex < cleanedText.length) {
    parseInlineMath(cleanedText.slice(lastIndex), parts);
  }
  
  // If no parts were added, treat entire text as plain text
  if (parts.length === 0 && cleanedText.trim()) {
    parts.push({ type: 'text', content: cleanedText });
  }
  
  return parts;
};

// Helper to parse inline math from text
const parseInlineMath = (text: string, parts: Array<{ type: 'text' | 'inline' | 'display'; content: string }>) => {
  if (!text) return;
  
  // Match inline math $...$ but not $$ and not escaped \$
  // Also avoid matching $ that's part of currency or standalone
  const inlineRegex = /(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)/g;
  
  let lastIndex = 0;
  let match;
  
  while ((match = inlineRegex.exec(text)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      if (textBefore.trim()) {
        parts.push({ type: 'text', content: textBefore });
      }
    }
    
    // Add inline math
    const mathContent = match[1].trim();
    if (mathContent && mathContent.length > 0) {
      parts.push({ type: 'inline', content: mathContent });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    const remaining = text.slice(lastIndex);
    if (remaining.trim()) {
      parts.push({ type: 'text', content: remaining });
    }
  }
  
  // If no matches found, add the entire text
  if (lastIndex === 0 && text.trim()) {
    parts.push({ type: 'text', content: text });
  }
};
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function SuperCalc() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { role, isLoading: roleLoading, hasAccess } = useUserRole();
  const { totalCredits, hasCredits, needsTopUp, loading: creditsLoading, refetch: refetchCredits } = useSupercalcCredits();
  const [query, setQuery] = useState("");
  const [solution, setSolution] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [isCheckingUsage, setIsCheckingUsage] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [responseStyle, setResponseStyle] = useState<"answer_only" | "full_explanation" | "lesson_plan" | "freestyle">("full_explanation");
  const [basicExamples] = useState([
    "Solve: x² + 5x + 6 = 0",
    "Calculate the derivative of x³ + 2x² - 5x + 3",
    "Find the area of a circle with radius 7.5 cm",
    "Integrate: ∫(2x + 3)dx",
    "What is the probability of rolling two 6s with two dice?",
  ]);
  const [advancedExamples] = useState([
    "Write me a report on the half-life of beta-adrenergic drugs, fully referenced and sourced",
    "I dug a pit 3.6m by 4.8m with a lean from 20cm depth to 60cm depth at the far side (4.8m). Help me work out how much soil I need to fill it in cubic meters",
    "Explain the thermodynamic principles behind the Carnot cycle with mathematical derivations",
    "Analyze the statistical significance of a clinical trial with 500 participants, 60% success rate, compared to 45% baseline",
  ]);

  const isFreeUser = role === 'free';
  const hasPaidAccess = hasAccess(['admin', 'paid', 'student']);

  useEffect(() => {
    const checkUsageAndBan = async () => {
      if (!user) {
        setIsCheckingUsage(false);
        return;
      }

      try {
        // Check if user is banned
        const { data: banData, error: banError } = await supabase
          .from('user_bans')
          .select('reason')
          .eq('user_id', user.id)
          .maybeSingle();

        if (banError && banError.code !== 'PGRST116') {
          console.error('Error checking ban status:', banError);
        }

        if (banData) {
          setIsBanned(true);
          setBanReason(banData.reason || 'No reason provided');
          setIsCheckingUsage(false);
          return;
        }

        // Check usage only for free users
        if (isFreeUser) {
          const { count, error } = await supabase
            .from('supercalc_usage' as any)
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);

          if (error) throw error;
          setUsageCount(count || 0);
        }
      } catch (error) {
        console.error('Error checking usage:', error);
      } finally {
        setIsCheckingUsage(false);
      }
    };

    checkUsageAndBan();
  }, [user, isFreeUser]);

  const solveProblem = async () => {
    if (!query.trim()) {
      toast.error("Please enter a mathematical query");
      return;
    }

    // Check if user is banned
    if (isBanned) {
      toast.error("You are banned from using SuperCalc");
      return;
    }

    // Check if free user has exceeded their trial
    if (isFreeUser && usageCount >= 1) {
      toast.error("You've used your free trial. Upgrade to continue!");
      return;
    }

    // Check if paid user has credits
    if (hasPaidAccess && !hasCredits && totalCredits <= 0) {
      toast.error("You're out of tokens! Purchase more to continue using SuperCalc.");
      return;
    }

    setIsLoading(true);
    setSolution("");

    try {
      const { data, error } = await supabase.functions.invoke("solve-math", {
        body: { query, responseStyle },
      });

      if (error) throw error;

      if (data?.error) {
        if (data.error.includes("insufficient credits") || data.error.includes("out of tokens")) {
          toast.error("You're out of tokens! Purchase more to continue.");
        } else {
          toast.error(data.error);
        }
        return;
      }

      setSolution(data.solution);
      
      // Refresh credits after successful query
      refetchCredits();
      
      // Update usage count for free users
      if (isFreeUser) {
        setUsageCount(prev => prev + 1);
      }
      
      if (isFreeUser && usageCount === 0) {
        toast.success("Problem solved! This was your free trial. Upgrade for unlimited access!");
      } else {
        toast.success("Problem solved!");
      }
    } catch (error) {
      console.error("Error solving problem:", error);
      toast.error("Failed to solve problem. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      solveProblem();
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportAsText = () => {
    if (!solution || !query) return;
    const content = `Problem:\n${query}\n\nSolution:\n${solution}`;
    downloadFile(content, 'supercalc-solution.txt', 'text/plain');
    toast.success('Exported as text file');
  };

  const exportAsLatex = () => {
    if (!solution || !query) return;
    const latexDocument = `\\documentclass{article}
\\usepackage{amsmath}
\\usepackage{amssymb}
\\title{SuperCalc Solution}
\\author{CalcHub}
\\date{\\today}

\\begin{document}
\\maketitle

\\section*{Problem}
${query}

\\section*{Solution}
${solution}

\\end{document}`;
    downloadFile(latexDocument, 'supercalc-solution.tex', 'application/x-latex');
    toast.success('Exported as LaTeX file');
  };

  const exportForGoogleDocs = () => {
    if (!solution || !query) return;
    
    // Convert solution to HTML for Google Docs
    const convertToHtml = (text: string): string => {
      let html = cleanResponseText(text);
      
      // Convert display math to styled blocks
      html = html.replace(/\$\$(.*?)\$\$/gs, '<p style="text-align: center; font-family: serif; font-size: 14pt; margin: 16px 0; background-color: #f5f5f5; padding: 12px; border-radius: 4px;">$1</p>');
      
      // Convert inline math to styled spans
      html = html.replace(/\$(.*?)\$/g, '<span style="font-family: serif; font-style: italic;">$1</span>');
      
      // Convert line breaks to paragraphs
      html = html.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
      
      return html;
    };
    
    const htmlContent = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>SuperCalc Solution</title>
<style>
body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
h1 { color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px; }
h2 { color: #555; margin-top: 24px; }
.math-block { text-align: center; font-family: 'Times New Roman', serif; font-size: 14pt; margin: 16px 0; background-color: #f8f9fa; padding: 12px; border-radius: 4px; }
.math-inline { font-family: 'Times New Roman', serif; font-style: italic; }
</style>
</head>
<body>
<h1>SuperCalc Solution</h1>
<h2>Problem</h2>
<p>${query}</p>
<h2>Solution</h2>
${convertToHtml(solution)}
<hr>
<p style="color: #888; font-size: 12px;">Generated by SuperCalc - CalcLab</p>
</body>
</html>`;
    
    downloadFile(htmlContent, 'supercalc-solution.html', 'text/html');
    toast.success('Exported as HTML - Open in browser, then copy to Google Docs');
  };

  const copyToClipboard = async () => {
    if (!solution || !query) return;
    
    // Create both plain text and HTML versions for rich clipboard
    const plainText = `Problem:\n${query}\n\nSolution:\n${cleanResponseText(solution)}`;
    
    const htmlContent = `<h2>Problem</h2><p>${query}</p><h2>Solution</h2><p>${cleanResponseText(solution).replace(/\n/g, '<br>')}</p>`;
    
    try {
      // Try to copy as rich text (HTML) for better Google Docs compatibility
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);
      toast.success('Copied as rich text - paste into Google Docs');
    } catch {
      // Fallback to plain text
      await navigator.clipboard.writeText(plainText);
      toast.success('Copied to clipboard');
    }
  };

  const saveToLibrary = async () => {
    if (!solution || !query) return;
    if (!user) {
      toast.error('Please sign in to save results');
      return;
    }
    if (role !== 'student' && role !== 'paid' && role !== 'admin') {
      toast.error('Library is a premium feature. Please upgrade your membership.');
      return;
    }

    const { error } = await supabase
      .from('library_items')
      .insert({
        user_id: user.id,
        result_type: 'SuperCalc',
        query,
        solution,
      });

    if (error) {
      console.error('Error saving to library:', error);
      toast.error('Failed to save to library');
    } else {
      toast.success('Result saved to library');
    }
  };

  if (authLoading || roleLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto mt-16 text-center space-y-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-4xl font-bold">Try SuperCalc Free!</h1>
          <p className="text-muted-foreground text-lg">
            Sign in to get <strong>1 free SuperCalc query</strong> and test our AI-powered math solver before upgrading.
          </p>
          <p className="text-sm text-muted-foreground">
            No credit card required • Try it risk-free
          </p>
          <Button size="lg" onClick={() => navigate("/auth")}>
            Sign In for Free Trial
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Block non-paying users who are not free tier
  if (!isFreeUser && !hasPaidAccess) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto mt-16 text-center space-y-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="h-12 w-12 text-accent" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-accent to-accent/80 bg-clip-text text-transparent">
            Premium Feature
          </h1>
          <p className="text-muted-foreground text-lg">
            SuperCalc is a premium AI-powered math solver available to paid members and students.
          </p>
          <Button size="lg" onClick={() => navigate("/membership")}>
            View Membership Options
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Block banned users
  if (isBanned) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto mt-16 text-center space-y-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
          </div>
          <h1 className="text-4xl font-bold text-destructive">
            Access Restricted
          </h1>
          <p className="text-muted-foreground text-lg">
            Your access to SuperCalc has been restricted by an administrator.
          </p>
          {banReason && (
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <p className="text-sm">
                  <strong>Reason:</strong> {banReason}
                </p>
              </CardContent>
            </Card>
          )}
          <p className="text-sm text-muted-foreground">
            If you believe this is a mistake, please contact support.
          </p>
          <Button variant="outline" onClick={() => navigate("/support")}>
            Contact Support
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Main UI for allowed users (free with remaining trial or paid/student)
  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto mt-8 space-y-6">
        {/* Upgrade banner for free users who've exhausted their trial */}
        {isFreeUser && usageCount >= 1 && (
          <Card className="border-accent">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-semibold text-lg mb-1">Free Trial Used</h3>
                  <p className="text-sm text-muted-foreground">
                    You've used your 1 free query. Upgrade for unlimited access!
                  </p>
                </div>
                <Button onClick={() => navigate("/membership")} className="shrink-0">
                  View Membership Options
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center mb-8">
          <div className="relative inline-block">
            <div className="absolute inset-0 blur-3xl opacity-40 bg-supercalc-brand" />
            <h1 className="relative text-5xl sm:text-6xl md:text-7xl font-bold font-museo text-supercalc-brand tracking-tighter" style={{ letterSpacing: '-0.08em' }}>
              SuperCalc
            </h1>
          </div>
        </div>

        {/* Token Balance Indicator for paid users */}
        {hasPaidAccess && !creditsLoading && (
          <Card className={`mb-4 ${needsTopUp ? 'border-amber-500/50 bg-amber-500/5' : 'border-primary/20'}`}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${needsTopUp ? 'bg-amber-500/20' : 'bg-primary/20'}`}>
                    <Coins className={`h-5 w-5 ${needsTopUp ? 'text-amber-500' : 'text-primary'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Token Balance</p>
                    <p className={`text-2xl font-bold ${needsTopUp ? 'text-amber-500' : 'text-foreground'}`}>
                      {totalCredits.toLocaleString()} tokens
                    </p>
                  </div>
                </div>
                {needsTopUp && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-amber-500 font-medium hidden sm:inline">Running low</span>
                    <Button size="sm" variant="outline" onClick={() => navigate("/membership")} className="border-amber-500 text-amber-500 hover:bg-amber-500/10">
                      Top Up
                    </Button>
                  </div>
                )}
                {!needsTopUp && (
                  <Button size="sm" variant="ghost" onClick={() => navigate("/membership")}>
                    Buy More
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              {isFreeUser && (
                <Badge variant={usageCount >= 1 ? "destructive" : "secondary"} className="ml-auto">
                  Free trial: {Math.max(0, 1 - usageCount)} left
                </Badge>
              )}
            </div>
            <Alert className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Disclaimer:</strong> Answers are generated by AI and should be verified. While our AI strives for accuracy, it may occasionally produce incorrect results or hallucinate. Always double-check important calculations.
              </AlertDescription>
            </Alert>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Ask a math question. Press Ctrl+Enter to solve:</label>
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Solve x² + 5x + 6 = 0, or explain the quadratic formula"
                className="min-h-[140px]"
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium block">Response Style:</label>
              <RadioGroup value={responseStyle} onValueChange={(value: any) => setResponseStyle(value)}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="answer_only" id="answer_only" />
                    <Label htmlFor="answer_only" className="cursor-pointer">Answer Only</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="full_explanation" id="full_explanation" />
                    <Label htmlFor="full_explanation" className="cursor-pointer">Full Explanation</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="lesson_plan" id="lesson_plan" />
                    <Label htmlFor="lesson_plan" className="cursor-pointer">Lesson Plan</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="freestyle" id="freestyle" />
                    <Label htmlFor="freestyle" className="cursor-pointer">Free-style</Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={solveProblem} disabled={isLoading} className="flex-1">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Solving...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Solve with SuperCalc
                  </>
                )}
              </Button>
            </div>

            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic">Basic Examples</TabsTrigger>
                <TabsTrigger value="advanced">Advanced Examples</TabsTrigger>
              </TabsList>
              <TabsContent value="basic" className="space-y-2 mt-4">
                <p className="text-sm text-muted-foreground">Try these examples:</p>
                <div className="flex flex-wrap gap-2">
                  {basicExamples.map((example, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => setQuery(example)}
                    >
                      {example}
                    </Badge>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="advanced" className="space-y-2 mt-4">
                <p className="text-sm text-muted-foreground">Advanced problems:</p>
                <div className="flex flex-col gap-2">
                  {advancedExamples.map((example, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="cursor-pointer hover:bg-accent transition-colors text-left py-2 px-3 h-auto whitespace-normal"
                      onClick={() => setQuery(example)}
                    >
                      {example}
                    </Badge>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {solution && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Solution</CardTitle>
              <div className="flex items-center gap-2">
                {hasPaidAccess && (
                  <Button variant="outline" size="sm" onClick={saveToLibrary}>
                    <Save className="h-4 w-4 mr-2" /> Save to Library
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" /> Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportAsText}>
                      <FileText className="h-4 w-4 mr-2" /> Text (.txt)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportAsLatex}>
                      <FileCode className="h-4 w-4 mr-2" /> LaTeX (.tex)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportForGoogleDocs}>
                      <FileText className="h-4 w-4 mr-2" /> HTML (Google Docs)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={copyToClipboard}>
                      <Copy className="h-4 w-4 mr-2" /> Copy to Clipboard
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose prose-invert max-w-none text-foreground leading-relaxed">
                {parseLatex(solution).map((part, index) => {
                  if (part.type === 'display') {
                    return (
                      <div key={index} className="my-6 overflow-x-auto bg-muted/30 p-4 rounded-lg">
                        <MathFormula block>{part.content}</MathFormula>
                      </div>
                    );
                  } else if (part.type === 'inline') {
                    return <MathFormula key={index}>{part.content}</MathFormula>;
                  } else {
                    return (
                      <div key={index} className="whitespace-pre-wrap">
                        {formatTextContent(part.content)}
                      </div>
                    );
                  }
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}

